import { useEffect, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/config';
import Button from './ui/Button';

interface InnerProps {
    clientSecret: string;
    mode: 'payment' | 'setup';
    onSuccess: (paymentMethodId?: string) => void;
    onCancel: () => void;
}

function PaymentElementInner({ clientSecret, mode, onSuccess, onCancel }: InnerProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!stripe || !elements) return;
        setIsSubmitting(true);
        try {
            if (mode === 'payment') {
                const { error } = await stripe.confirmPayment({
                    elements,
                    confirmParams: {},
                    redirect: 'if_required'
                });
                if (error) throw error;
                onSuccess();
            } else {
                const { setupIntent, error } = await stripe.confirmSetup({
                    elements,
                    confirmParams: {},
                    redirect: 'if_required'
                });
                if (error) throw error;
                onSuccess(setupIntent?.payment_method as string | undefined);
            }
        } catch (err) {
            console.error('[PaymentElement] submit error', err);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement />
            <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
                <Button type="submit" disabled={!stripe || isSubmitting}>{mode === 'payment' ? 'Pay' : 'Save'}</Button>
            </div>
        </form>
    );
}

export default function PaymentElementModal({ clientSecret, mode, onSuccess, onCancel }: {
    clientSecret: string;
    mode: 'payment' | 'setup';
    onSuccess: (paymentMethodId?: string) => void;
    onCancel: () => void;
}) {
    const [options, setOptions] = useState<any>();
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        setOptions({ clientSecret, appearance: { theme: 'stripe' } });
    }, [clientSecret]);

    // Native <dialog>/showModal(): top-layer stacking so this can never render
    // behind a fixed-position sibling (e.g. the cookie consent banner) regardless
    // of z-index, matching the LaunchPopup/MyPage precedent (TSF-114/115). This
    // component is only mounted by its parent while clientSecret is set, so open
    // once `options` (and therefore the <dialog> element) exists.
    useEffect(() => {
        if (options) dialogRef.current?.showModal();
    }, [options]);

    if (!clientSecret || !options) return null;

    return (
        <dialog
            ref={dialogRef}
            onCancel={onCancel}
            onClick={(e) => { if (e.target === dialogRef.current) onCancel(); }}
            className="m-auto max-w-md w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/40"
        >
            <div className="bg-white rounded-lg shadow-lg w-full p-6">
                <Elements stripe={getStripe()} options={options}>
                    <PaymentElementInner clientSecret={clientSecret} mode={mode} onSuccess={onSuccess} onCancel={onCancel} />
                </Elements>
            </div>
        </dialog>
    );
}


